import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DepreciationOpenings, TaxSettings, TaxSummary } from '@vuokramani/shared';
import { api } from './api.ts';

const BASE = import.meta.env.VITE_API_URL ?? '/api';

/** Vuosiraportti: PDF (kuitteineen) tai CSV (Excel). Ilman asuntoa kaikki asunnot. */
export const taxReportUrl = (year: number, format: 'pdf' | 'csv', propertyId?: string) =>
  `${BASE}/taxes/report?year=${year}&format=${format}${propertyId ? `&propertyId=${propertyId}` : ''}`;

/** Vuoden veroluvut asunnoittain (lomake 7H / OmaVero: Vuokratulot). */
export function useTaxes(year: number) {
  return useQuery({
    queryKey: ['taxes', year],
    queryFn: () => api<TaxSummary>(`/taxes?year=${year}`),
    // Laskut ja kulut muuttuvat muilla sivuilla: haetaan aina sivulle tultaessa.
    staleTime: 0,
  });
}

export function useSaveTaxSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ propertyId, body }: { propertyId: string; body: TaxSettings }) =>
      api<void>(`/properties/${propertyId}/tax-settings`, { method: 'PUT', body }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['taxes'] }),
  });
}

/** Ennen Vuokranttia alkaneet poistot. */
export function useDepreciationOpenings(propertyId: string, enabled = true) {
  return useQuery({
    queryKey: ['depreciationOpenings', propertyId],
    queryFn: () => api<DepreciationOpenings>(`/properties/${propertyId}/depreciation-openings`),
    enabled,
  });
}

export function useSaveDepreciationOpenings(propertyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: DepreciationOpenings) =>
      api<DepreciationOpenings>(`/properties/${propertyId}/depreciation-openings`, {
        method: 'PUT',
        body,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['depreciationOpenings', propertyId] });
      void queryClient.invalidateQueries({ queryKey: ['taxes'] });
    },
  });
}
